import type { AcousticSceneInput } from '../acoustics/compute'
import { clampToRoom } from '../acoustics/room'
import type { SceneDoc, Vec3 } from '../model/schema'

/** Read every nested property so the surrounding Solid effect re-runs on any change. */
export function track(value: unknown): void {
  if (value && typeof value === 'object') {
    for (const k in value as Record<string, unknown>) track((value as Record<string, unknown>)[k])
  }
}

export const clone = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T

/** Plain (non-proxy) description of what the acoustic worker needs. */
export function toAcousticScene(doc: SceneDoc): AcousticSceneInput {
  return {
    enclosure: clone({
      shape: doc.enclosure.shape,
      size: doc.enclosure.size,
      core: doc.enclosure.core,
      outline: doc.enclosure.outline,
      materials: doc.enclosure.materials,
    }),
    objects: doc.objects.map((o) =>
      clone({
        id: o.id,
        name: o.name,
        kind: o.kind,
        position: o.position,
        rotation: o.rotation,
        size: o.size,
        material: o.material,
        wall: o.wall,
        caps: o.caps,
        outline: o.outline,
        holes: o.holes,
      }),
    ),
    listener: [...doc.listener.position] as Vec3,
    sources: doc.sources.map((s) => ({ id: s.id, position: [...s.position] as Vec3, size: s.size })),
  }
}

export function clampInside(doc: SceneDoc, p: Vec3, margin = 0.05): Vec3 {
  return clampToRoom(doc.enclosure, p, margin)
}
/** 440 → "440 Hz", 2500 → "2.5 kHz". */
export const formatHz = (hz: number) => (hz >= 1000 ? `${round(hz / 1000, 1)} kHz` : `${Math.round(hz)} Hz`)

export const deg = (r: number) => (r * 180) / Math.PI
export const rad = (d: number) => (d * Math.PI) / 180
export const round = (v: number, places = 2) => {
  const f = 10 ** places
  return Math.round(v * f) / f
}
